import React, { useState } from 'react';
import { Upload, AlertCircle, CheckCircle, Loader2, FileSpreadsheet, Building2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { compressLogo } from '../../utils/imageUtils';
interface LogoData {
  name: string;
  url: string;
}
interface BulkImportLogosProps {
  onImport: (logos: LogoData[]) => Promise<void>;
}
export function BulkImportLogos({
  onImport
}: BulkImportLogosProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [result, setResult] = useState<{
    success: number;
    errors: string[];
  } | null>(null);
  const handleFilesSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      setSelectedFiles(files);
      setResult(null);
    }
  };
  const handleBulkImport = async () => {
    if (selectedFiles.length === 0) {
      alert('Por favor selecciona al menos una imagen');
      return;
    }
    setIsProcessing(true);
    setResult(null);
    const logos: LogoData[] = [];
    const errors: string[] = [];
    try {
      for (const file of selectedFiles) {
        try {
          // Usar compressLogo para preservar transparencia
          const compressedUrl = await compressLogo(file);
          // Extraer nombre del archivo (sin extensión)
          const fileName = file.name.replace(/\.[^/.]+$/, '');
          const cleanName = fileName.replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          logos.push({
            name: cleanName,
            url: compressedUrl
          });
        } catch (error) {
          errors.push(`Error procesando ${file.name}: ${error}`);
        }
      }
      if (logos.length > 0) {
        await onImport(logos);
        setResult({
          success: logos.length,
          errors
        });
        setSelectedFiles([]);
        // Reset file input
        const fileInput = document.getElementById('bulk-logo-upload') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
      } else {
        setResult({
          success: 0,
          errors: ['No se pudieron procesar las imágenes']
        });
      }
    } catch (error: any) {
      setResult({
        success: 0,
        errors: [error.message || 'Error al importar logos']
      });
    } finally {
      setIsProcessing(false);
    }
  };
  return <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-6" data-id="element-103">
      <div className="flex items-start gap-3 mb-6" data-id="element-104">
        <div className="p-2 bg-purple-50 rounded-lg" data-id="element-105">
          <Building2 className="h-5 w-5 text-purple-600" data-id="element-106" />
        </div>
        <div className="flex-1" data-id="element-107">
          <h3 className="text-lg font-bold text-gray-900" data-id="element-108">
            Importación Masiva de Logos
          </h3>
          <p className="text-sm text-gray-500 mt-1" data-id="element-109">
            Sube múltiples logos PNG con fondo transparente. Se convertirán a
            blanco y negro automáticamente.
          </p>
        </div>
      </div>

      {/* Instrucciones */}
      <div className="mb-6 bg-purple-50 border border-purple-200 rounded-lg p-4" data-id="element-110">
        <div className="flex items-start gap-2 mb-3" data-id="element-111">
          <FileSpreadsheet className="h-4 w-4 text-purple-600 mt-0.5" data-id="element-112" />
          <p className="text-sm font-semibold text-purple-900" data-id="element-113">
            Cómo funciona la importación masiva:
          </p>
        </div>

        <ol className="text-sm text-purple-800 space-y-2 list-decimal list-inside" data-id="element-114">
          <li data-id="element-115">
            Selecciona múltiples archivos{' '}
            <strong data-id="element-116">PNG con fondo transparente</strong>
          </li>
          <li data-id="element-117">
            El nombre del archivo se usará como nombre del cliente (ej:
            "banco-chile.png" → "Banco Chile")
          </li>
          <li data-id="element-118">Las imágenes se optimizarán manteniendo la transparencia</li>
          <li data-id="element-119">
            Los logos se mostrarán en blanco y negro en el carrusel del home
          </li>
          <li data-id="element-120">Después de importar, puedes editar nombres individualmente</li>
        </ol>

        <div className="mt-4 bg-white rounded-lg p-3 border border-purple-200" data-id="element-121">
          <p className="text-xs font-bold text-gray-900 mb-2" data-id="element-122">
            ⚠️ CRÍTICO - Requisitos de los logos:
          </p>
          <ul className="text-xs text-gray-700 space-y-1 list-disc list-inside" data-id="element-123">
            <li data-id="element-124">
              <strong className="text-red-600" data-id="element-125">SOLO archivos PNG</strong> - Los
              JPG tendrán fondo blanco
            </li>
            <li data-id="element-126">
              <strong data-id="element-127">Fondo transparente obligatorio</strong> - Verifica en
              Photoshop/Illustrator antes de exportar
            </li>
            <li data-id="element-128">Alta resolución (se optimizarán automáticamente a 600px)</li>
            <li data-id="element-129">
              Nombra los archivos descriptivamente (ej: "scotiabank.png",
              "banco-chile.png")
            </li>
            <li data-id="element-130">Puedes seleccionar hasta 50 logos a la vez</li>
          </ul>
        </div>
      </div>

      {/* File Selection */}
      <div className="space-y-4" data-id="element-131">
        <div data-id="element-132">
          <label htmlFor="bulk-logo-upload" className="block text-sm font-medium text-gray-700 mb-3" data-id="element-133">
            Selecciona múltiples logos PNG
          </label>

          <div className="flex flex-col gap-3" data-id="element-134">
            <label htmlFor="bulk-logo-upload" className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-lg border-2 border-dashed border-purple-300 cursor-pointer hover:border-purple-500 hover:bg-purple-50 transition-colors bg-white" data-id="element-135">
              <Upload className="h-5 w-5 text-purple-500" data-id="element-136" />
              <span className="text-sm font-medium text-gray-700" data-id="element-137">
                {selectedFiles.length > 0 ? `${selectedFiles.length} archivo(s) seleccionado(s)` : 'Seleccionar logos PNG (múltiples archivos)'}
              </span>
              <input id="bulk-logo-upload" type="file" accept="image/png,image/svg+xml" multiple className="hidden" onChange={handleFilesSelect} disabled={isProcessing} data-id="element-138" />
            </label>

            {selectedFiles.length > 0 && <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-id="element-139">
                <p className="text-sm font-semibold text-gray-900 mb-2" data-id="element-140">
                  Archivos seleccionados ({selectedFiles.length}):
                </p>
                <div className="max-h-40 overflow-y-auto" data-id="element-141">
                  <ul className="text-xs text-gray-700 space-y-1" data-id="element-142">
                    {selectedFiles.map((file, index) => <li key={index} className="flex items-center gap-2" data-id="element-143">
                        <span className="text-purple-600" data-id="element-144">•</span>
                        <span className="truncate" data-id="element-145">{file.name}</span>
                        <span className={`text-xs font-medium ${file.type === 'image/png' ? 'text-green-600' : 'text-red-600'}`} data-id="element-146">
                          {file.type === 'image/png' ? '✓ PNG' : '⚠️ NO ES PNG'}
                        </span>
                        <span className="text-gray-500" data-id="element-147">
                          ({(file.size / 1024).toFixed(1)} KB)
                        </span>
                      </li>)}
                  </ul>
                </div>
              </div>}
          </div>
        </div>

        {/* Result Message */}
        {result && <div className={`p-4 rounded-lg ${result.errors.length > 0 ? 'bg-yellow-50 border border-yellow-200' : 'bg-green-50 border border-green-200'}`} data-id="element-148">
            <div className="flex items-start gap-3" data-id="element-149">
              {result.errors.length > 0 ? <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" data-id="element-150" /> : <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" data-id="element-151" />}
              <div className="flex-1" data-id="element-152">
                {result.success > 0 && <p className="text-sm font-semibold text-green-900 mb-2" data-id="element-153">
                    ✅ {result.success} logo(s) importado(s) exitosamente
                  </p>}
                {result.errors.length > 0 && <>
                    <p className="text-sm font-semibold text-yellow-900 mb-2" data-id="element-154">
                      ⚠️ Algunos archivos tuvieron errores:
                    </p>
                    <ul className="text-sm text-yellow-800 list-disc list-inside max-h-32 overflow-y-auto" data-id="element-155">
                      {result.errors.map((error, i) => <li key={i} data-id="element-156">{error}</li>)}
                    </ul>
                  </>}
              </div>
            </div>
          </div>}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200" data-id="element-157">
          <Button variant="secondary" onClick={() => {
          setSelectedFiles([]);
          setResult(null);
          const fileInput = document.getElementById('bulk-logo-upload') as HTMLInputElement;
          if (fileInput) fileInput.value = '';
        }} disabled={isProcessing} data-id="element-158">
            Limpiar
          </Button>
          <Button onClick={handleBulkImport} disabled={isProcessing || selectedFiles.length === 0} leftIcon={isProcessing ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-160" /> : <Upload className="h-4 w-4" data-id="element-161" />} className="bg-purple-600 hover:bg-purple-700" data-id="element-159">
            {isProcessing ? `Procesando ${selectedFiles.length} logo(s)...` : `Importar ${selectedFiles.length} Logo(s)`}
          </Button>
        </div>
      </div>
    </div>;
}